import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@auditwire/contracts';
import { Button, Field, Input } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useAuth } from '../useAuth';

/** Seeded demo accounts — shown only in dev builds (never on the public deploy). */
const DEMO_ACCOUNTS = [
  { label: 'Admin', email: 'admin@auditwire.com', password: 'admin123' },
  { label: 'Writer', email: 'writer@auditwire.com', password: 'writer123' },
  { label: 'Reader', email: 'reader@auditwire.com', password: 'reader123' },
] as const;

export function LoginForm({ onSuccess }: { onSuccess?: () => void }) {
  const { signIn } = useAuth();
  const [formError, setFormError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const runSignIn = async (input: LoginInput) => {
    setFormError('');
    try {
      await signIn(input);
      onSuccess?.();
    } catch (err) {
      setFormError(toMessage(err, 'Could not sign in. Check your details and try again.'));
    }
  };

  return (
    <form onSubmit={handleSubmit(runSignIn)} className="space-y-4" noValidate>
      {formError ? (
        <p
          role="alert"
          className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400"
        >
          {formError}
        </p>
      ) : null}

      <Field label="Email" htmlFor="login-email" error={errors.email?.message}>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...register('email')}
        />
      </Field>

      <Field label="Password" htmlFor="login-password" error={errors.password?.message}>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          {...register('password')}
        />
      </Field>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Signing in…' : 'Sign in'}
      </Button>

      {import.meta.env.DEV ? (
        <div className="border-t border-border pt-3">
          <p className="mb-2 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Demo accounts
          </p>
          <div className="grid grid-cols-3 gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                onClick={() => void runSignIn({ email: account.email, password: account.password })}
                disabled={isSubmitting}
                className="rounded-lg border border-border py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-zinc-50 hover:text-foreground disabled:opacity-50 dark:hover:bg-zinc-800"
              >
                {account.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </form>
  );
}
